import { ChangeEvent, useMemo, useState, FocusEvent } from "react";

import { isAfter } from "date-fns";

import TextInput from "@components/TextInput";
import useUpdateValue from "@hooks/useUpdateValue";
import parseDate from "@lib/date/parseDate";
import { useRouter } from "@lib/router";
import { withCompositeFormikField } from "@lib/withFormikField";

import Calendar from "./Calendar";
import type { TDatePickerMode, TDatePickerProps, TDateValue } from "./types";
import formatDateToTemplate from "./utils/formatDateToTemplate";
import parseRange from "./utils/parseRange";

const DEFAULT_DATE_TEMPLATE = "dd.MM.yyyy";

const DatePicker = <T extends TDatePickerMode>({
  id,
  mode,
  value,
  defaultValue,
  onChange,
  onBlur,
  setFieldValue,
  minDate,
  maxDate,
  filterDate,
  isDisabled,
  isInvalid,
  message,
  locale,
  selectYearFrom,
  selectYearTo,
  onClose,
  dateTemplate = DEFAULT_DATE_TEMPLATE,
  paddingInline,
  ...rest
}: TDatePickerProps<T>) => {
  const initialValue = useMemo(
    () => ({
      value: value || defaultValue,
      text: formatDateToTemplate(value || defaultValue, dateTemplate),
    }),
    [dateTemplate, defaultValue, value]
  );
  const [isOpen, setIsOpen] = useState(false);
  const [innerValue, updateInnerValue] = useUpdateValue(initialValue);
  const [invalidFormatMessage, setInvalidFormatMessage] = useState<string>();

  const { locale: routerLocale } = useRouter();

  if (minDate && maxDate && isAfter(minDate, maxDate)) {
    throw new Error("maxDate prop must be after minDate");
  }

  const withParsedDate = (handler) => (event: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>) => {
    const { value: inputValue } = event.target;

    const parseFunc = mode === "range" ? parseRange : parseDate;
    const parsedDate = parseFunc(inputValue, dateTemplate, minDate, maxDate) as TDateValue<T> | null;

    handler(inputValue, parsedDate);
  };

  const handleInputChange = (inputValue, parsedDate) => {
    // Remove validation message onChange only if it changes to correct one
    // If you need validation only on blur - remove 2 lines below
    if (parsedDate && !!inputValue) {
      setInvalidFormatMessage(undefined);
    }

    updateInnerValue({ value: parsedDate || undefined, text: inputValue });

    if (parsedDate) {
      setFieldValue?.(parsedDate);
      onChange?.(parsedDate);
    }

    if (!inputValue) {
      setFieldValue?.(null);
      onChange?.(null);
    }
  };

  const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    onBlur?.(event);
  };

  const handleOnClose = () => {
    onClose?.();
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleDateChange = (newValue: TDateValue<T> | null) => {
    if (!newValue) {
      updateInnerValue({ value: undefined, text: "" });
      setFieldValue?.(newValue);
      onChange?.(newValue);
      return;
    }
    updateInnerValue({ value: newValue, text: formatDateToTemplate(newValue, dateTemplate) });
    setFieldValue?.(newValue);
    onChange?.(newValue);
  };

  const calendar = (
    <Calendar
      id={id}
      isDisabled={isDisabled}
      mode={mode}
      value={innerValue?.value}
      onChange={handleDateChange}
      onClose={handleOnClose}
      minDate={minDate}
      maxDate={maxDate}
      isOpen={isOpen}
      onToggleOpen={setIsOpen}
      filterDate={filterDate}
      locale={locale || routerLocale}
      selectYearFrom={selectYearFrom}
      selectYearTo={selectYearTo}
    />
  );

  return (
    <TextInput
      id={id}
      value={innerValue?.text || ""}
      onFocus={handleInputFocus}
      onChange={withParsedDate(handleInputChange)}
      onBlur={handleInputBlur}
      {...rest}
      isReadOnly
      isInvalid={isInvalid || !!invalidFormatMessage}
      message={message || invalidFormatMessage}
      isDisabled={isDisabled}
      paddingInline={paddingInline}
      isFormik={false}
      rightAddon={calendar}
      rightSpacing="1px"
      rightAddonOffset="2.75rem"
      // because off doesn't fully prevent autocomplete
      autoComplete="none"
      autoFocus={false}
      role="combobox"
      spellCheck="false"
      autoCapitalize="none"
      autoCorrect="off"
    />
  );
};

export default withCompositeFormikField({ hasSetFieldValue: true, hasOnChangeCallback: false })(DatePicker);
